import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

const TagInsertContext = createContext(null);

export const TagInsertProvider = ({ children }) => {
  const [activeField, setActiveField] = useState(null);
  const activeFieldRef = useRef(null);
  const cursorPositionRef = useRef({ start: 0, end: 0 });

  // Register a field as the active target for tag insertion
  const registerField = useCallback((fieldElement, fieldId) => {
    activeFieldRef.current = fieldElement;
    setActiveField(fieldId);

    // Store cursor position
    if (fieldElement) {
      cursorPositionRef.current = {
        start: fieldElement.selectionStart || 0,
        end: fieldElement.selectionEnd || 0,
      };
    }
  }, []);

  // Update cursor position when selection changes
  const updateCursorPosition = useCallback((fieldElement) => {
    if (fieldElement && activeFieldRef.current === fieldElement) {
      cursorPositionRef.current = {
        start: fieldElement.selectionStart || 0,
        end: fieldElement.selectionEnd || 0,
      };
    }
  }, []);

  // Insert tag at cursor position in the active field
  const insertTag = useCallback((tagText) => {
    const field = activeFieldRef.current;

    if (!field) {
      // No field registered, fallback to clipboard
      navigator.clipboard.writeText(tagText);
      return { inserted: false, copied: true };
    }

    const { start, end } = cursorPositionRef.current;
    const currentValue = field.value || '';

    // Insert tag at cursor position
    const newValue =
      currentValue.substring(0, start) +
      tagText +
      currentValue.substring(end);

    // For React controlled components, we need to use the native setter
    // to properly trigger React's synthetic event system
    const isTextarea = field.tagName.toLowerCase() === 'textarea';
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      isTextarea ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype,
      'value'
    )?.set;

    if (nativeInputValueSetter) {
      // Use native setter to bypass React's value tracking
      nativeInputValueSetter.call(field, newValue);
    } else {
      // Fallback for environments where native setter isn't available
      field.value = newValue;
    }

    // Dispatch input event to trigger React's onChange
    const inputEvent = new Event('input', { bubbles: true, cancelable: true });
    field.dispatchEvent(inputEvent);

    // Also dispatch change event as some handlers listen to this
    const changeEvent = new Event('change', { bubbles: true, cancelable: true });
    field.dispatchEvent(changeEvent);

    // Move cursor after the inserted tag
    const newCursorPos = start + tagText.length;

    // Use requestAnimationFrame to ensure the value is set before we move cursor
    requestAnimationFrame(() => {
      if (field && document.body.contains(field)) {
        field.setSelectionRange(newCursorPos, newCursorPos);
        field.focus();
      }
    });

    // Update stored cursor position
    cursorPositionRef.current = {
      start: newCursorPos,
      end: newCursorPos,
    };

    return { inserted: true, copied: false, newValue };
  }, []);

  // Clear the active field
  const clearActiveField = useCallback(() => {
    activeFieldRef.current = null;
    setActiveField(null);
  }, []);

  return (
    <TagInsertContext.Provider
      value={{
        activeField,
        registerField,
        updateCursorPosition,
        insertTag,
        clearActiveField,
        hasActiveField: !!activeFieldRef.current,
      }}
    >
      {children}
    </TagInsertContext.Provider>
  );
};

export const useTagInsert = () => {
  const context = useContext(TagInsertContext);
  if (!context) {
    throw new Error('useTagInsert must be used within a TagInsertProvider');
  }
  return context;
};

// Custom hook for input/textarea elements to register with the context
export const useTaggableInput = (fieldId) => {
  const { registerField, updateCursorPosition } = useTagInsert();
  const inputRef = useRef(null);

  const handleFocus = useCallback((e) => {
    registerField(e.target, fieldId);
  }, [registerField, fieldId]);

  const handleSelect = useCallback((e) => {
    updateCursorPosition(e.target);
  }, [updateCursorPosition]);

  const handleClick = useCallback((e) => {
    updateCursorPosition(e.target);
  }, [updateCursorPosition]);

  const handleKeyUp = useCallback((e) => {
    updateCursorPosition(e.target);
  }, [updateCursorPosition]);

  return {
    ref: inputRef,
    onFocus: handleFocus,
    onSelect: handleSelect,
    onClick: handleClick,
    onKeyUp: handleKeyUp,
  };
};

export default TagInsertContext;
